import { Sparkles, ArrowRight, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import LRDirectTemplatesCard from "@/components/lr-direct/atlas/LRDirectTemplatesCard";
import LRDirectAtlasStatusBar from "@/components/lr-direct/atlas/LRDirectAtlasStatusBar";
import LRDirectRepricingCard from "@/components/lr-direct/atlas/LRDirectRepricingCard";
import LRDirectVoicePostCard from "@/components/lr-direct/atlas/LRDirectVoicePostCard";
import LRDirectBrokerMatchCard from "@/components/lr-direct/atlas/LRDirectBrokerMatchCard";
import LRDirectLaneOptimizerCard from "@/components/lr-direct/atlas/LRDirectLaneOptimizerCard";
import LRDirectRateGuardCard from "@/components/lr-direct/atlas/LRDirectRateGuardCard";
import LRDirectBulkUploadCard from "@/components/lr-direct/atlas/LRDirectBulkUploadCard";

/**
 * LR Direct ATLAS Command Center.
 * Independent sibling of the LaneRunner ATLAS Hub — mirrors its visual pattern only.
 * No imports from src/components/ai-post/*, LR Direct data sources only.
 */
export default function LRDirectAtlasPage() {
  usePageMeta({
    title: "ATLAS Command Center",
    description: "LR Direct tools hub — posting templates and LR Direct automation.",
  });
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-card relative">
      {/* Page header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 bg-card border-b border-border">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 min-w-0">
          <Zap className="h-4 w-4 shrink-0" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          <h1 className="text-base font-semibold tracking-wide uppercase text-foreground">LR Direct</h1>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground font-medium">
            <Sparkles className="h-3.5 w-3.5 text-brand-teal-deep shrink-0" />
            ATLAS Command Center
          </span>
        </div>
        <Button
          size="sm"
          className="h-9 sm:h-8 w-full sm:w-auto text-xs gap-1.5 shrink-0"
          onClick={() => navigate("/lr-direct/post/start")}
        >
          <Zap className="h-3.5 w-3.5" />
          New LR Direct Post
        </Button>
      </div>

      {/* Market / context strip */}
      <LRDirectAtlasStatusBar />

      <div className="px-4 py-2 bg-card/60 border-b border-border">
        <p className="text-xs text-muted-foreground">
          Your LR Direct tools in one place. Reuse saved lanes to post loads and quote requests in seconds.
        </p>
      </div>

      <div className="flex-1 px-4 py-4 space-y-3 max-w-[1600px] mx-auto w-full">
        {/* Full-width, fully functional templates card */}
        <LRDirectTemplatesCard />

        {/* Tool card grid — visual placeholders until LR Direct logic ships */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">
          <LRDirectRepricingCard />
          <LRDirectVoicePostCard />
          <LRDirectBrokerMatchCard />
          <LRDirectLaneOptimizerCard />
          <LRDirectRateGuardCard />
          <LRDirectBulkUploadCard />
        </div>
      </div>
    </div>
  );
}
